import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { isSessionTokenExpired } from 'src/app/shared/utils/session-age';
import { AlertService } from 'src/app/shared/services/alert.service';
import { loginReturnParams } from 'src/app/shared/utils/session-tabs';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private isLoggingOut = false;

  /**
   * Verificación de sesión en curso.
   *
   * Un 401 rara vez viene solo: el home dispara varias llamadas a la vez y el
   * polling suma más. Sin este candado, cada una preguntaría por su cuenta y
   * cada pregunta puede terminar en un `Auth.currentSession()` contra Cognito.
   */
  private verificacion: Promise<void> | null = null;

  /**
   * Momento del último cierre de sesión, y ventana de gracia posterior.
   *
   * Las peticiones que estaban en vuelo cuando se cerró la sesión resuelven
   * su 401 unos milisegundos después, ya sin sesión que verificar. Sin esta
   * ventana cada una abriría su propio "Sesión expirada" encima del login.
   */
  private ultimoLogoutAt = 0;
  private static GRACIA_MS = 15_000;

  /** Tope para que ni el cierre ni la navegación puedan colgar la salida. */
  private static SALIDA_TIMEOUT_MS = 5_000;

  constructor(
    private cognito: CognitoService,
    private router: Router,
    private alerts: AlertService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

      // Peticiones marcadas con X-Skip-Error-Interceptor no pasan por este interceptor
      if (request.headers.has('X-Skip-Error-Interceptor')) {
        const clean = request.clone({ headers: request.headers.delete('X-Skip-Error-Interceptor') });
        return next.handle(clean);
      }

      return next.handle(request).pipe(catchError(err => {

          if (err.status === 401) {
              // El corte por tope de 12 h ya avisa y redirige por su cuenta.
              // Sin esto se superponían dos modales sobre la misma salida.
              if (this.cognito.isForcingLogout()) {
                  return throwError(() => err);
              }

              const isPermissionError = err.error?.title === 'Unauthorized'
                && err.error?.message;

              if (isPermissionError) {
                  Swal.fire({
                    icon: 'warning',
                    title: 'Sin permisos',
                    text: err.error.message,
                    confirmButtonColor: '#1e5a3f',
                  });
              } else {
                  // No se cierra sesión acá: un 401 no prueba que la sesión
                  // murió. Se decide después de mirar el token, y sin frenar
                  // esta respuesta — el error se relanza igual que siempre.
                  void this.cerrarSesionSiVencio(request, err);
              }
          }

          if (err.status === 403) {
              Swal.fire({
                icon: 'warning',
                title: 'Acceso denegado',
                text: err.error?.message || 'No tienes permisos para realizar esta acción',
                confirmButtonColor: '#1e5a3f',
              });
          }

          // Preservar el error completo para que los componentes accedan a todos los detalles.
          // 400 = bad request, 422 = validation/business rule (ej: payin no llegó al banco),
          // 429 = rate limit. En estos casos el backend devuelve un mensaje específico que el
          // componente debe poder mostrar al usuario sin que el interceptor lo aplane a string.
          if (err.status === 429 || err.status === 400 || err.status === 422) {
              return throwError(() => err);
          }

          const error = err.error?.message || err.statusText;
          return throwError(() => error);

      }))
  }

  /**
   * Cierra la sesión SÓLO si de verdad se cayó.
   *
   * El 401 no distingue "se te venció la sesión" de "el servidor no pudo
   * validar este token ahora". El guard de Cognito del wallet-api envuelve
   * todo en un try/catch y devuelve 401 "Authentication failed" ante cualquier
   * tropiezo —incluido no poder traer las claves públicas en una instancia
   * recién levantada—, y el authorizer de API Gateway devuelve 401 pelado. En
   * los dos casos la sesión del usuario está intacta, y echarlo al login es
   * inventarle un problema que no tiene.
   *
   * La regla es simple: si el token sigue vivo, esto no fue la sesión. Se
   * relanza el error para que el componente lo muestre y no se toca nada más.
   */
  private cerrarSesionSiVencio(request: HttpRequest<any>, err: any): Promise<void> {
    if (this.isLoggingOut) return Promise.resolve();
    if (Date.now() - this.ultimoLogoutAt < ErrorInterceptor.GRACIA_MS) {
      return Promise.resolve();
    }
    if (this.verificacion) return this.verificacion;

    this.verificacion = this.evaluar(request, err)
      // Esto se lanza sin `await`, así que un rechazo acá quedaría como
      // promesa sin atender y ensuciaría la consola justo cuando importa.
      .catch(() => undefined)
      .finally(() => {
        this.verificacion = null;
      });

    return this.verificacion;
  }

  private async evaluar(request: HttpRequest<any>, err: any): Promise<void> {
    const viva = await this.sesionSigueViva();

    // Mientras se preguntaba pudo entrar el corte por tope de 12 h, que trae
    // su propio aviso. O otro 401 que ya cerró la sesión.
    if (this.cognito.isForcingLogout() || this.isLoggingOut) return;

    if (viva) {
      this.registrar(request, err, 'sesión válida, NO se cierra sesión');
      return;
    }

    if (this.registroEnCurso()) {
      this.registrar(request, err, 'registro en curso, NO se cierra sesión');
      return;
    }

    this.registrar(request, err, 'sin sesión, se cierra sesión');
    await this.forzarLogout();
  }

  /**
   * `true` si el usuario está en medio del alta.
   *
   * Ahí no hay sesión de dashboard que proteger y sí hay mucho que perder:
   * `signOut()` termina en `localStorage.clear()`, que se lleva el estado del
   * wizard —quince pasos, correo y WhatsApp ya verificados— y deja al usuario
   * en el login sin nada que retomar.
   *
   * Y entre el paso 2 y el 3 no hay sesión todavía: el alta en Cognito ocurre
   * antes del `signIn`, así que ahí `getSession()` devuelve null de manera
   * legítima. Sin este corte, un 401 en esa ventana —la reserva de la llave
   * sale justo ahí— se leía como sesión vencida y borraba el registro entero.
   *
   * Los pasos manejan sus propios errores; el wizard se retoma solo.
   */
  private registroEnCurso(): boolean {
    try {
      if (sessionStorage.getItem('aguacate_registration_in_progress') === 'true') {
        return true;
      }
    } catch {
      /* Safari en modo privado */
    }
    return this.router.url.startsWith('/auth');
  }

  /**
   * `true` si el token con el que se firman las peticiones sigue vigente.
   *
   * `getSession()` cachea 30 s, así que puede devolver una sesión cuyo token
   * ya venció. En ese caso se vuelve a preguntar sin caché antes de echar a
   * nadie: Amplify refresca solo, y la respuesta que vale es la de después
   * del refresh.
   */
  private async sesionSigueViva(): Promise<boolean> {
    try {
      let session = await this.cognito.getSession();

      if (session && isSessionTokenExpired(session)) {
        this.cognito.clearCache();
        session = await this.cognito.getSession();
      }

      return !isSessionTokenExpired(session);
    } catch {
      // Si no se puede ni preguntar, se trata como sesión caída: el camino
      // conservador acá es el login, no dejar al usuario adentro a ciegas.
      return false;
    }
  }

  /**
   * Cierra la sesión y devuelve al login.
   *
   * Navega ANTES de avisar y no espera el clic: si otro `Swal.fire` abre un
   * popup encima, sweetalert2 saca el anterior del DOM sin resolver su
   * promesa. Con el `.then()` de por medio eso dejaba la redirección sin
   * hacer y `isLoggingOut` colgado en true para siempre, y a partir de ahí
   * ningún 401 volvía a avisar nada.
   */
  private async forzarLogout(): Promise<void> {
    // Entre la verificación y acá, otra pestaña pudo volver a entrar. Cerrar
    // ahora borraría esa sesión nueva de todas las pestañas; se suelta el
    // caché y la próxima petición sale con la sesión nueva.
    if (this.cognito.hayLoginMasNuevo()) {
      this.cognito.clearCache();
      return;
    }

    this.isLoggingOut = true;
    this.ultimoLogoutAt = Date.now();
    // Se anota antes de salir: al volver a entrar, esta pestaña regresa acá.
    const volver = this.router.url;

    try {
      await this.conTope(this.cognito.signOut());
    } catch {
      /* signOut() ya limpia el storage en su propio catch */
    }

    try {
      await this.conTope(
        this.router.navigate(['/auth/login'], {
          queryParams: loginReturnParams(volver),
        }),
      );
    } catch {
      /* si el router no está listo, el aviso alcanza */
    }

    // Se refresca al final para que la gracia cubra todo el cierre, no sólo
    // su primer instante.
    this.ultimoLogoutAt = Date.now();
    this.isLoggingOut = false;

    // `critico`: es lo único que puede desplazar un modal abierto. Dejar al
    // usuario tocando botones de una sesión que ya no existe es peor que
    // interrumpirle lo que estaba leyendo.
    this.alerts.fire(
      {
        icon: 'info',
        title: 'Sesión expirada',
        text: 'Tu sesión ha expirado, por favor inicia sesión nuevamente.',
        confirmButtonColor: '#1e5a3f',
      },
      'critico',
    );
  }

  /**
   * Resuelve igual si la promesa se cuelga.
   *
   * `signOut()` pide el usuario a Cognito por red y la navegación puede
   * quedarse esperando un guard. Si alguna de las dos no vuelve, sin tope se
   * quedaba `isLoggingOut` en true para siempre: ni redirección, ni aviso, y
   * ningún 401 posterior vuelve a avisar nada. Es el mismo tope que usa el
   * corte por edad de sesión.
   */
  private conTope<T>(promise: Promise<T>): Promise<T | null> {
    return Promise.race([
      promise,
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), ErrorInterceptor.SALIDA_TIMEOUT_MS),
      ),
    ]);
  }

  /**
   * Deja en consola quién devolvió el 401 y qué se hizo con él.
   *
   * Sin esto, un reporte de "me dice sesión expirada" no trae con qué
   * empezar. Va método, URL y el mensaje del backend; nunca el token ni el
   * cuerpo completo, que puede traer datos del usuario.
   */
  private registrar(request: HttpRequest<any>, err: any, decision: string): void {
    console.warn(
      `[401] ${request.method} ${request.url} → ${decision}`,
      { status: err?.status, message: err?.error?.message ?? err?.statusText },
    );
  }
}
